import { filter, isArray, isNil } from "lodash";

import {
  compileUnescapedTextTemplate,
  extractTemplateVariables,
  filterActiveDropdownItems,
  isSearchTokensMatch,
  Log,
  type IDropdownItem,
  type IDropdownItemExt,
  type IDropdownItemFilter,
  type IRadioGroupItem,
  type ISelectListItem,
  type ISelectListItemExt,
} from "@ryde/shared";

import type {
  CustomDropdownItemsFilterType,
  TransformLabelFnType,
} from "../../../controls";

/**
 * The value an option is keyed by: numeric strings become numbers.
 * Booleans stay as-is: `+true` is `1`, so a `true` form value would find no option (label "undefined").
 */
export const getDropdownOptionValue = ({
  value,
}: {
  value: string | number | boolean;
}): string | number | boolean =>
  typeof value === "boolean" ? value : +value || value;

/** Code name per option value, for the items that carry one. */
export const getOptionTechnicalNames = (
  selectItems: ISelectListItem[] | undefined,
): Map<string | number | boolean, string> =>
  new Map(
    (selectItems ?? []).flatMap(item =>
      item.technicalName
        ? [[getDropdownOptionValue(item), item.technicalName] as const]
        : [],
    ),
  );

/** Keeps the options whose label or code name holds every typed term. */
export const filterOptionsByLabelOrTechnicalName = <T>({
  options,
  inputValue,
  getOptionLabel,
  technicalNames,
}: {
  options: T[];
  inputValue: string;
  getOptionLabel: (option: T) => string;
  technicalNames: ReadonlyMap<unknown, string>;
}): T[] =>
  options.filter(option =>
    isSearchTokensMatch({
      texts: [getOptionLabel(option), technicalNames.get(option)],
      searchValue: inputValue,
    }),
  );

export const getDropdownValuesLabels = ({
  dropDownItems,
  transformLabelFn,
  customDropdownItemsFilter,
}: {
  dropDownItems:
    IDropdownItem[] | ISelectListItem[] | IRadioGroupItem[] | undefined;
  transformLabelFn?: TransformLabelFnType;
  customDropdownItemsFilter?: CustomDropdownItemsFilterType;
}): [
  (string | number | boolean)[] | undefined,
  Map<string | number | boolean, string>,
] => {
  if (!dropDownItems) {
    return [undefined, new Map()];
  }

  let tempDropDownItems = dropDownItems;

  if (
    customDropdownItemsFilter &&
    typeof customDropdownItemsFilter === "function"
  ) {
    tempDropDownItems = tempDropDownItems?.filter(item =>
      customDropdownItemsFilter(item as IDropdownItemExt),
    );
  }

  const [options, mapOptionLabel] = tempDropDownItems.reduce<
    [(string | number | boolean)[], Map<string | number | boolean, string>]
  >(
    ([optionsAcc, mapOptionLabelAcc], item) => {
      const value = getDropdownOptionValue(item);

      if (mapOptionLabelAcc.has(value)) {
        return [optionsAcc, mapOptionLabelAcc];
      }

      optionsAcc.push(value);
      mapOptionLabelAcc.set(
        value,
        transformLabelFn ? transformLabelFn(item) : item.label || "",
      );

      return [optionsAcc, mapOptionLabelAcc];
    },
    [[], new Map()],
  );

  return [options, mapOptionLabel];
};

interface IGetDropdownOptionsArguments {
  ownFieldName?: string;
  dropdownItems?: IDropdownItemExt[] | ISelectListItemExt[];
  customActiveFieldName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentFieldValue: any;
  sourceFieldValue: any;
  filters: IDropdownItemFilter["filters"];
  excludeIds?: (string | number | boolean)[];
  customDropdownItemsFilter?: CustomDropdownItemsFilterType;
  transformLabelFn?: TransformLabelFnType;
  // When set, keep only options whose value is in this list (e.g. the selected shift's mealIds).
  allowedValues?: (string | number)[];
  /** Assigned inactive/orphan values stay visible after chips are trimmed. */
  bulkAssignedValuesSet?: Set<string>;
}

export const getDropdownOptions = ({
  ownFieldName,
  customActiveFieldName,
  dropdownItems,
  currentFieldValue,
  sourceFieldValue,
  filters,
  excludeIds,
  customDropdownItemsFilter,
  transformLabelFn,
  allowedValues,
  bulkAssignedValuesSet,
}: IGetDropdownOptionsArguments): [
  (string | number | boolean)[] | undefined,
  Map<string | number | boolean, string>,
] => {
  const dropdownItemsFiltered = filterActiveDropdownItems({
    items: dropdownItems,
    activeFieldName: customActiveFieldName,
    isSelected: item =>
      bulkAssignedValuesSet?.has(String(item.value)) ||
      String(item.value) === String(currentFieldValue) ||
      currentFieldValue?.includes?.(item.value),
  });

  let filteredItems =
    ownFieldName && !isNil(sourceFieldValue)
      ? dropdownItemsFiltered?.filter(
          item =>
            sourceFieldValue === item[ownFieldName] || !item[ownFieldName],
        )
      : dropdownItemsFiltered;

  if (allowedValues?.length) {
    const allowedValuesSet = new Set(allowedValues.map(String));
    filteredItems = filteredItems?.filter(item =>
      allowedValuesSet.has(String(item.value)),
    );
  }

  if (filters) {
    filteredItems = filter(filteredItems, filters) as ISelectListItemExt[];
  }

  if (
    customDropdownItemsFilter &&
    typeof customDropdownItemsFilter === "function"
  ) {
    filteredItems = filteredItems?.filter(customDropdownItemsFilter);
  }

  if (!isArray(dropdownItemsFiltered) || !filteredItems?.length) {
    return [undefined, new Map()];
  }

  if (excludeIds?.length) {
    const excludeIdsSet = new Set(excludeIds);
    filteredItems = filteredItems.filter(
      i => !excludeIdsSet.has(+i.value || i.value),
    );
  }

  const [options, mapOptionLabel] = getDropdownValuesLabels({
    dropDownItems: filteredItems,
    transformLabelFn,
  });

  return [options, mapOptionLabel];
};

export const dropDownBoundTemplateFormat = ({
  dropDownTemplate,
  itemData = {},
}: {
  dropDownTemplate: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemData?: Record<string, any>;
}): string => {
  try {
    return compileUnescapedTextTemplate({
      template: dropDownTemplate,
      data: itemData,
    });
  } catch (err) {
    Log.error(`Error compiling template: ${dropDownTemplate}`, err);

    const invalidKeys = extractTemplateVariables(dropDownTemplate)?.filter(
      key => !(key in itemData),
    );

    if (invalidKeys?.length > 0) {
      Log.error(`Invalid template: ${invalidKeys.join(", ")}`);
    }

    return itemData?.label ?? "";
  }
};
